import { Container } from '@acies/core';
import { theme } from '@acies/ui';
import AnswersSection from './answers-section/answers-section';
import BottomBar from './bottom-bar/bottom-bar';
import ConfiguratorMachine from './configurator.machine';
import GeometryOverlay from './dialogs/geometry-overlay';
import DrawingSection from './drawing-section/drawing-section';
import PositionsSection from './positions-section/positions-section';
import PropertiesPanel from './properties-panel/properties-panel';
import TopBar from './top-bar/top-bar';

export default class Configurator extends Container {
	#topBar!: TopBar;
	#bottomBar!: BottomBar;
	#center!: Container;
	#positionsSection!: PositionsSection;
	#rightContainer!: Container;
	#answersSection!: AnswersSection;
	#drawingSection!: DrawingSection;
	#propertiesPanel!: PropertiesPanel;
	#geometryOverlay!: GeometryOverlay;
	#machine!: ConfiguratorMachine;
	public constructor() {
		super();
		this.width = 'fill';
		this.height = 'fill';
		this.paddingHorizontal = 8;
		this.paddingVertical = 8;
		this.autoLayout = 'vertical';
		this.gapVertical = 8;
		this.fill = theme.colors.fill.weak;
	}

	public get topBar(): TopBar {
		if (!this.#topBar) {
			this.#topBar = new TopBar();
		}
		return this.#topBar;
	}

	public get center(): Container {
		if (!this.#center) {
			this.#center = new Container();
			this.#center.width = 'fill';
			this.#center.height = 'fill';
			this.#center.gapHorizontal = 8;
			this.#center.addComponents([this.positionsSection, this.rightContainer, this.propertiesPanel]);
		}
		return this.#center;
	}

	public get positionsSection(): PositionsSection {
		if (!this.#positionsSection) {
			this.#positionsSection = new PositionsSection();
		}
		return this.#positionsSection;
	}

	public get rightContainer(): Container {
		if (!this.#rightContainer) {
			this.#rightContainer = new Container();
			this.#rightContainer.width = 816;
			this.#rightContainer.height = 'fill';
			this.#rightContainer.autoLayout = 'vertical';
			this.#rightContainer.gapVertical = 8;
			this.#rightContainer.addComponents([this.answersSection, this.drawingSection]);
		}
		return this.#rightContainer;
	}

	public get answersSection(): AnswersSection {
		if (!this.#answersSection) {
			this.#answersSection = new AnswersSection();
		}
		return this.#answersSection;
	}

	public get drawingSection(): DrawingSection {
		if (!this.#drawingSection) {
			this.#drawingSection = new DrawingSection();
		}
		return this.#drawingSection;
	}

	public get propertiesPanel(): PropertiesPanel {
		if (!this.#propertiesPanel) {
			this.#propertiesPanel = new PropertiesPanel();
		}
		return this.#propertiesPanel;
	}

	/** What the Configurator does with what its components report. */
	public get machine(): ConfiguratorMachine {
		return this.#machine;
	}

	/**
	 * Stops the machine when the Configurator leaves the document.
	 *
	 * The machine holds a gathering window and may have a drawing on its way, and neither knows the screen has gone — see {@link ConfiguratorMachine.stop} for what is held back and what is let land. A Configurator put back into the document keeps this stopped machine: initialize() built it once and runs once, and
	 * nothing here starts it again.
	 */
	protected override disconnected(): void {
		super.disconnected();
		this.#machine.stop();
	}

	public get bottomBar(): BottomBar {
		if (!this.#bottomBar) {
			this.#bottomBar = new BottomBar();
		}
		return this.#bottomBar;
	}

	/**
	 * The overlay the geometry dialog sits in, where the shape of a position is picked.
	 *
	 * The last child, so nothing drawn after it can end up on top of it. It is always there and shows or hides itself from `ConfiguratorModel.geometryDialogShown`, rather than being added when it is asked for and removed again.
	 */
	public get geometryOverlay(): GeometryOverlay {
		if (!this.#geometryOverlay) {
			this.#geometryOverlay = new GeometryOverlay();
		}
		return this.#geometryOverlay;
	}

	protected override initialize(): void {
		super.initialize();
		this.addComponents([this.topBar, this.center, this.bottomBar, this.geometryOverlay]);
		// built last, so everything the machine listens to is already there to listen to
		this.#machine = new ConfiguratorMachine(this);
	}
}
customElements.define('ac-configurator', Configurator);
